import React from "react";
import TopAppBar from "./TopAppBar";
import { Params, Path } from "@/router";
import { LinkProps } from "@generouted/react-router/client";
import { SnackbarProvider } from "notistack";
import { snackbarProviderComponents } from "@/components/Snackbar";
import ScrollToTop from "@/components/ScrollToTop";
import { MenuItem } from "@/components/Menu";
import type { Action } from "@/components/MenuButton";

type FocusedPageLayoutProps<P extends Path> = {
  backLink?: LinkProps<P, Params>;
  /** When set, runs instead of navigating via `backLink`; `backLink` still supplies the label and fallback route. */
  onBackClick?: () => void;
  cancelButton?: React.ReactNode;
  /** Workflow chrome that always stays inline (Save, Continue, Previous). Not condensed. */
  actions?: React.ReactNode;
  /**
   * Object/page commands rendered through ActionCluster. The first eligible
   * action stays inline when three or more are present; the rest collapse
   * into a MenuButton. On compact layouts, a pair collapses into an Actions
   * menu instead of splitting. Prefer this over `actions` for command bars.
   * Compact array `actions` fold into this cluster when every item has a
   * plain-text label, so the bar never shows two overflow menus. If any item
   * cannot convert, the original `actions` nodes stay visible.
   */
  clusterActions?: Action[];
  extendedActions?: MenuItem[];
  renderBanner?: () => React.ReactNode;
  stickyActions?: boolean;
};

export default function FocusedPageLayout<P extends Path>({
  children,
  backLink,
  onBackClick,
  cancelButton,
  actions,
  clusterActions,
  extendedActions = [],
  stickyActions = false,
  renderBanner
}: React.PropsWithChildren<FocusedPageLayoutProps<P>>) {
  return (
    <SnackbarProvider
      Components={snackbarProviderComponents}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right"
      }}
    >
      <ScrollToTop />
      <main className="flex min-h-screen">
        <div className="w-full">
          <TopAppBar
            backLink={backLink}
            onBackClick={onBackClick}
            actions={actions}
            clusterActions={clusterActions}
            cancelButton={cancelButton}
            extendedActions={extendedActions}
            stickyActions={stickyActions}
          />
          {renderBanner && renderBanner()}
          <div className="mx-6 md:mx-12 mb-24">
            <div className="max-w-[1440px] mx-auto">{children}</div>
          </div>
        </div>
      </main>
    </SnackbarProvider>
  );
}
